import styles from "./style.module.css";

import { Link } from "react-router-dom";
import { Box, Grid, Typography, Button, CircularProgress } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import AdminApi from "../../../../config/apis/admin";
import { api, matchingApi } from "../../../../config/apis";
import { DataLoader, GetUserId, IsStudent } from "../../../../helper/helperFunction";

const JobSingleCard = () => {
  const userIsStudent = IsStudent();
  const userId = GetUserId();

  const [jobData, setJobData] = useState();
  const [loading, setLoading] = useState(true);
  const [aiRecommendationsByJobId, setAiRecommendationsByJobId] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoaded, setAiLoaded] = useState(false);

  const displayJobData = useMemo(() => {
    const jobs = Array.isArray(jobData) ? jobData : [];
    if (!userIsStudent || !aiLoaded || !jobs.length) return jobs;

    const indexed = jobs.map((job, idx) => ({ job, idx }));
    indexed.sort((a, b) => {
      const ra = aiRecommendationsByJobId?.[a.job?.id]?.rank;
      const rb = aiRecommendationsByJobId?.[b.job?.id]?.rank;
      if (ra == null && rb == null) return a.idx - b.idx;
      if (ra == null) return 1;
      if (rb == null) return -1;
      if (ra !== rb) return ra - rb;
      return a.idx - b.idx;
    });
    return indexed.map((x) => x.job);
  }, [aiLoaded, aiRecommendationsByJobId, jobData, userIsStudent]);

  const fetchAiRecommendations = async (jobsOnPage) => {
    if (!userIsStudent || !userId) return;
    const jobs = Array.isArray(jobsOnPage) ? jobsOnPage : [];
    const topN = jobs.length || 0;
    if (topN <= 0) return;

    const res = await matchingApi.get(`recommendations/students/${userId}/jobs`, {
      params: {
        top_n: topN,
        location_priority: true,
        job_type_priority: true,
        include_inactive: false,
        skip_llm: false,
      },
    });

    const list = Array.isArray(res?.data?.recommendations)
      ? res.data.recommendations
      : [];

    const nextMap = {};
    list.forEach((rec, index) => {
      if (rec?.job_id == null) return;
      nextMap[rec.job_id] = {
        rank: index + 1,
        final_score: rec?.final_score,
        missing_skills: rec?.missing_skills,
      };
    });

    const jobsOnPageSet = new Set(
      jobs.map((j) => j?.id).filter((id) => id != null)
    );
    const topRecOnPage = list.find((rec) => jobsOnPageSet.has(rec?.job_id));
    if (topRecOnPage?.job_id != null && nextMap[topRecOnPage.job_id]) {
      nextMap[topRecOnPage.job_id].is_top = true;
    }

    setAiRecommendationsByJobId(nextMap);
    setAiLoaded(true);
  };

  useEffect(() => {
    let ignore = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        if (userIsStudent) {
          setAiRecommendationsByJobId({});
          setAiLoaded(false);
        }

        const res = await api.get(AdminApi.ACTIVE_JOB_LIST);

        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (ignore) return;

        setJobData(data);
      } catch (error) {
        console.error(error);
        if (!ignore) setJobData([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchData();

    return () => {
      ignore = true;
    };
  }, [userIsStudent, userId]);

  const handleLoadAiRecommendations = async () => {
    if (!userIsStudent || !userId) return;
    if (aiLoading) return;

    const jobs = Array.isArray(jobData) ? jobData : [];
    if (!jobs.length) return;

      try {
        setAiLoading(true);
        setAiRecommendationsByJobId({});
        setAiLoaded(false);

        const statusRes = await matchingApi.get(`resumes/status/${userId}`);
        const status = statusRes?.data;
        if (!status?.is_ready) {
          const ps = status?.processing_status;
          let message;
          if (ps === "no_resume") {
            message = status?.message || "Please upload a resume first.";
          } else if (ps === "failed") {
            message = status?.message || "AI processing failed. Please try again.";
          } else {
            message = "AI is enhancing your resume for better matching..";
          }
          toast.info(message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
          });
          return;
        }

        await fetchAiRecommendations(jobs);
      } catch (error) {
        console.error(error);
        setAiRecommendationsByJobId({});
        setAiLoaded(false);
      } finally {
        setAiLoading(false);
      }
    };

  return (
    <>
      {userIsStudent && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleLoadAiRecommendations}
              disabled={loading || aiLoading || !Array.isArray(jobData)}
              sx={{
                textTransform: "none",
                backgroundColor: "#001C28",
                "&:hover": { backgroundColor: "#00131c" },
              }}
            >
              {aiLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                  Loading AI...
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 18, color: "#fff" }} />
                  AI Recommendations
                </Box>
              )}
            </Button>
          </Box>
        </Grid>
      )}
      {loading ? (
        <DataLoader />
      ) : (
        displayJobData &&
        displayJobData.map((item) => {
          return (
            <JobCard
              item={item}
              key={item?.id ?? `${item?.job_title || "job"}`}
              lg={4}
              aiEnabled={aiLoaded}
              recommendation={
                userIsStudent ? aiRecommendationsByJobId?.[item?.id] : null
              }
            />
          );
        })
      )}
    </>
  );
};

const formatScore = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Number.isInteger(n) ? `${n}%` : `${n.toFixed(1)}%`;
};

const normalizeStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => String(v || "").trim())
    .filter(Boolean);
};

const JobCard = ({ item, lg = 6, recommendation = null, aiEnabled = true }) => {
  const isRecommended = aiEnabled && !!recommendation;
  const isTopRecommended =
    recommendation?.is_top === true || recommendation?.rank === 1;
  const tagLabel = isTopRecommended ? "Top Recommended" : "Recommended";
  const scoreText = formatScore(recommendation?.final_score);
  const missingSkills = normalizeStringArray(recommendation?.missing_skills);
  const missingSkillsPreview = missingSkills.slice(0, 4);
  const missingSkillsExtraCount = Math.max(
    0,
    missingSkills.length - missingSkillsPreview.length
  );

  return (
    <Grid item xs={12} lg={lg} className={styles.col}>
      <Box className={styles.col_wrap}>
        <Link className={styles.Link} to={`/admin/jobs/view/${item.id}`}></Link>

        <Box display={"flex"} justifyContent={"space-between"}>
          <Box className={styles.titleWrap}>
            <Typography variant="h3">{item.job_title}</Typography>
            {isRecommended && (
              <span
                className={
                  isTopRecommended ? styles.topRecommendedTag : styles.recommendedTag
                }
              >
                {tagLabel}
              </span>
            )}
          </Box>
          <Box display={"flex"} className={styles.edit_wrap}>
            <span className={styles.active}>
              {item.job_status ? "Active" : "InActive"}
            </span>
          </Box>
        </Box>
        <Typography variant="h4">{item.company_name}</Typography>
        <Typography variant="p">{item.company_overview}</Typography>

        {aiEnabled && !!recommendation && (
          <Box className={styles.aiMeta}>
            {scoreText && (
              <span className={styles.matchScoreTag}>{`Match: ${scoreText}`}</span>
            )}
            <Box className={styles.missingSkillsWrap}>
              <span className={styles.missingSkillsLabel}>Missing:</span>
              {missingSkillsPreview.length ? (
                <Box className={styles.missingSkillsList}>
                  {missingSkillsPreview.map((skill, idx) => (
                    <span
                      className={styles.skillPill}
                      key={`missing-dashboard-${item.id}-${idx}-${skill}`}
                    >
                      {skill}
                    </span>
                  ))}
                  {missingSkillsExtraCount > 0 && (
                    <span className={styles.skillPill}>
                      +{missingSkillsExtraCount} more
                    </span>
                  )}
                </Box>
              ) : (
                <span className={styles.mutedValue}>-</span>
              )}
            </Box>
          </Box>
        )}

        <Box>
          <Box className={styles.small_box}>
            <span className={styles.key}>Applicants</span>
            <span className={styles.value}>{item.applicant_count}</span>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

export default JobSingleCard;
